import { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import "./CarouselBanner.css";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MissingContent from "../errorMessages/MissingContent";
import Hls from 'hls.js';

// Define the types directly in this file
interface HeroImage {
  filename?: string;
  url: string;
}

interface VideoData {
  video_url: string;
  thumbnail_url?: string;
}

interface CarouselBannerProps {
  heroImages?: Array<HeroImage>;
  heroVideo?: VideoData | null;
  heroLink?: string;
  videoLink?: string;
  promoImages?: Array<HeroImage>;
  promoLink?: string;
  displayOption?: string;
  aspectRatio?: string; // Keep for backward compatibility
  bannerWidth?: string | number | null; // Add banner width
  bannerHeight?: string | number | null; // Add banner height
}

const CarouselBanner = ({
                          heroImages = [],
                          heroVideo = null,
                          heroLink = '',
                          videoLink = '',
                          promoImages = [],
                          promoLink = '',
                          displayOption = 'carousel',
                          aspectRatio = "56.25%", // Default 16:9 aspect ratio (fallback)
                          bannerWidth = null,
                          bannerHeight = null
                        }: CarouselBannerProps) => {

  // Calculate dynamic aspect ratio from banner dimensions
  const calculateAspectRatio = (): string => {
    // Check if both width and height are valid (truthy and not 0)
    const width = bannerWidth && Number(bannerWidth);
    const height = bannerHeight && Number(bannerHeight);

    if (width && height && width > 0 && height > 0) {
      // Calculate aspect ratio as percentage: (height / width) * 100
      const calculatedRatio = (height / width) * 100;
      return `${calculatedRatio}%`;
    }

    // Fallback to default aspect ratio
    return aspectRatio;
  };

  const dynamicAspectRatio = calculateAspectRatio();

  const convertToHlsUrl = (url: string): string => {
    if (url.includes('.m3u8')) {
      return url;
    }

    const videoExtRegex = /\.(mp4|mov|avi|webm|mkv|flv)(\?.*)?$/i;

    if (videoExtRegex.test(url)) {
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      const dimensionMatch = filename.match(/_(\d+x\d+px)\.(mp4|mov|avi|webm|mkv|flv)(\?.*)?$/i);

      if (dimensionMatch) {
        const dimensions = dimensionMatch[1];
        const queryParams = dimensionMatch[3] || '';
        const beforeDimensions = filename.split(`_${dimensions}.`)[0];

        let actualName = '';

        // Remove CMS prefix if it exists
        if (beforeDimensions.match(/^cms_[a-f0-9\-]+_/i)) {
          actualName = beforeDimensions.replace(/^cms_[a-f0-9\-]+_/i, '');
        } else {
          actualName = beforeDimensions;
        }

        const hlsFilename = `${actualName}_${dimensions}_master.m3u8${queryParams}`;
        const basePath = urlParts.slice(0, -1).join('/');
        const encodedFilename = encodeURIComponent(hlsFilename);
        return `${basePath}/${encodedFilename}`;
      }

      // Fallback
      const hlsUrl = url.replace(
        /\.(mp4|mov|avi|webm|mkv|flv)(\?.*)?$/i,
        '_master.m3u8$2'
      );
      return hlsUrl;
    }

    return url;
  };

  // Safely handle video content with HLS conversion
  const videoItem = (heroVideo && heroVideo.video_url && (!displayOption || displayOption === 'video'))
    ? [{
      url: convertToHlsUrl(heroVideo.video_url),
      isVideo: true,
      link: videoLink || ''
    }]
    : [];

  // Safely handle image content based on display option
  const getImageItems = () => {
    if (displayOption === 'promo' && promoImages && promoImages.length > 0) {
      return promoImages.filter(Boolean).map((img: HeroImage) => ({
        url: img.url,
        isVideo: false,
        link: promoLink || ''
      }));
    }

    if (displayOption === 'carousel' || !displayOption ||
      (displayOption === 'video' && !heroVideo) ||
      (displayOption === 'promo' && (!promoImages || promoImages.length === 0))) {
      return (heroImages || []).filter(Boolean).map((img: HeroImage) => ({
        url: img.url,
        isVideo: false,
        link: heroLink || ''
      }));
    }

    return [];
  };

  const imageItems = getImageItems();

  // Combine items with null check
  const initialItems = displayOption === 'video' ? [...videoItem, ...imageItems] : [...imageItems, ...videoItem];

  interface CarouselItem {
    url: string;
    isVideo: boolean;
    link: string;
  }

  const [items, setItems] = useState<Array<CarouselItem>>(initialItems);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hlsFailedRef = useRef<boolean>(false);

  useEffect(() => {
    // Safely update items when props change
    const newVideoItem = (heroVideo?.video_url && (!displayOption || displayOption === 'video'))
      ? [{
        url: convertToHlsUrl(heroVideo.video_url),
        isVideo: true,
        link: videoLink || ''
      }]
      : [];

    const newImageItems = getImageItems();
    const newItems = displayOption === 'video' ? [...newVideoItem, ...newImageItems] : [...newImageItems, ...newVideoItem];
    setItems(newItems);

    // Reset current index if it's out of bounds
    if (currentIndex >= newItems.length) {
      setCurrentIndex(0);
    }
  }, [heroImages, heroVideo, heroLink, videoLink, promoImages, promoLink, displayOption]);

  const next = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  const previous = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  // Optimized preloading to only load what's needed
  useEffect(() => {
    if (items.length === 0) return;

    // Only preload current and next items
    const itemsToPreload = [
      items[currentIndex],
      items[(currentIndex + 1) % items.length]
    ].filter(Boolean);

    itemsToPreload.forEach((item) => {
      if (!item?.url || item.isVideo) return;

      const img = new Image();
      img.src = item.url;
    });
  }, [items, currentIndex]);



// HLS effect
  useEffect(() => {
    const currentItem = items[currentIndex];

    if (!currentItem?.isVideo || !videoRef.current) {
      return;
    }

    const videoElement = videoRef.current;
    const mp4Url = heroVideo?.video_url;

    // If no MP4 exists, just try playing whatever URL we have
    if (!mp4Url) {
      videoElement.src = currentItem.url;
      videoElement.play();
      return;
    }

    const isHLS = currentItem.url.includes('.m3u8');

    // MP4 exists and we have HLS URL - try HLS first
    if (isHLS) {
      // If HLS already failed before, skip straight to MP4
      if (hlsFailedRef.current) {
        videoElement.src = mp4Url;
        videoElement.play();
        return;
      }

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          maxLoadingDelay: 1,
          manifestLoadingTimeOut: 2000,
          manifestLoadingMaxRetry: 0,
          levelLoadingTimeOut: 2000,
          levelLoadingMaxRetry: 0,
          fragLoadingTimeOut: 2000,
          fragLoadingMaxRetry: 0,
        });

        hls.loadSource(currentItem.url);
        hls.attachMedia(videoElement);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          videoElement.play();
        });

        hls.on(Hls.Events.ERROR, () => {
          hlsFailedRef.current = true;
          hls.destroy();
          hlsRef.current = null;
          videoElement.src = mp4Url;
          videoElement.play();
        });

        hlsRef.current = hls;
      } else {
        // HLS not supported, use MP4
        videoElement.src = mp4Url;
        videoElement.play();
      }
    } else {
      // Already MP4 URL, just play it
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      videoElement.src = currentItem.url;
      videoElement.play();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [currentIndex, items, heroVideo]);

  const handleVideoEnd = () => {
    if (items.length > 1 && displayOption !== 'video') {
      next();
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        console.warn('Video replay failed');
      });
    }
  };

  useEffect(() => {
    console.log('=== INTERNATIONAL CAROUSEL DEBUG ===');
    console.log('heroVideo:', heroVideo);
    console.log('heroVideo.video_url:', heroVideo?.video_url);
    console.log('Items:', items);
    console.log('Current item:', items[currentIndex]);
  }, [heroVideo, items, currentIndex]);

  const handleItemClick = (item: CarouselItem) => {
    if (item?.link) {
      window.location.href = item.link;
    }
  };

  const showNavigation = items.length > 1 &&
    !(displayOption === 'video' && items[0]?.isVideo) &&
    displayOption !== 'promo';

  return (
    <Box>
      {items.length === 0 ? (
        <MissingContent sectionName="Banner Carousel" />
      ) : (
        <Box
          sx={{
            width: "100%",
            position: "relative",
            overflow: "hidden",
            paddingTop: dynamicAspectRatio, // Use calculated aspect ratio with fallback
          }}
        >
          {items.map((item, index) => (
            item && (
              <Box
                key={index}
                className={`slide ${index === currentIndex ? "active" : ""}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  cursor: item.link ? "pointer" : "default",
                  transition: displayOption === 'promo' ? 'opacity 0.3s ease-in-out' : 'opacity 0.5s ease-in-out',
                }}
                onClick={() => handleItemClick(item)}
              >
                {item.isVideo ? (
                  <video
                    ref={index === currentIndex ? videoRef : null}
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      objectFit: "contain",
                    }}
                    onEnded={handleVideoEnd}
                    onDragStart={(e) => e.preventDefault()}
                    muted
                    autoPlay
                    playsInline
                    // Add poster image
                    poster={heroVideo?.thumbnail_url || ''}
                    // Only preload metadata initially
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      objectFit: "contain",
                    }}
                    src={item.url}
                    alt=""
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  />
                )}
              </Box>
            )
          ))}
          {showNavigation && (
            <>
              <ArrowBackIosIcon
                sx={{
                  position: "absolute",
                  bottom: "50%",
                  left: "5%",
                  zIndex: 1,
                  cursor: "pointer",
                }}
                onClick={previous}
              />
              <ArrowForwardIosIcon
                sx={{
                  position: "absolute",
                  bottom: "50%",
                  right: "5%",
                  zIndex: 1,
                  cursor: "pointer",
                }}
                onClick={next}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default CarouselBanner;